import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

const AddSymptomDialog = ({ open, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    description: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    ongoing: true,
    severity: '',
    what_helps: '',
    what_worsens: '',
    medications_taken: '',
    medications_helped: '',
    related_conditions: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onAdd({
        ...formData,
        severity: formData.severity || undefined,
        end_date: formData.end_date || undefined,
      });

      setFormData({
        description: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        ongoing: true,
        severity: '',
        what_helps: '',
        what_worsens: '',
        medications_taken: '',
        medications_helped: '',
        related_conditions: '',
        notes: '',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Symptom</DialogTitle>
          <DialogDescription>Record a sign or symptom</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="description">Symptom Description *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="severity">Severity</Label>
                <Select value={formData.severity} onValueChange={(value) => setFormData({ ...formData, severity: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mild">Mild</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="severe">Severe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="ongoing"
                checked={formData.ongoing}
                onCheckedChange={(checked) => setFormData({ ...formData, ongoing: checked })}
              />
              <Label htmlFor="ongoing" className="font-normal cursor-pointer">Ongoing (not resolved)</Label>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="what_helps">What Makes It Better</Label>
              <Textarea
                id="what_helps"
                value={formData.what_helps}
                onChange={(e) => setFormData({ ...formData, what_helps: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="what_worsens">What Makes It Worse</Label>
              <Textarea
                id="what_worsens"
                value={formData.what_worsens}
                onChange={(e) => setFormData({ ...formData, what_worsens: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="medications_taken">Medications Already Taken</Label>
              <Textarea
                id="medications_taken"
                value={formData.medications_taken}
                onChange={(e) => setFormData({ ...formData, medications_taken: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
              {loading ? 'Adding...' : 'Add Symptom'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSymptomDialog;